import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { BookOpen, Plus, Trash2, Smile, Meh, Frown, Zap, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

export default function JournalPage() {
  const { getAuthHeader, API } = useAuth();
  const { t, i18n } = useTranslation();
  
  const moodOptions = [
    { value: 'energized', label: i18n.language === 'no' ? 'Energisk' : 'Energized', icon: Zap, color: 'text-primary' },
    { value: 'positive', label: i18n.language === 'no' ? 'Positiv' : 'Positive', icon: Smile, color: 'text-secondary' },
    { value: 'neutral', label: i18n.language === 'no' ? 'Nøytral' : 'Neutral', icon: Meh, color: 'text-muted-foreground' },
    { value: 'struggling', label: i18n.language === 'no' ? 'Sliter' : 'Struggling', icon: Frown, color: 'text-orange-500' },
    { value: 'grateful', label: i18n.language === 'no' ? 'Takknemlig' : 'Grateful', icon: Heart, color: 'text-pink-500' }
  ];

  const prompts = i18n.language === 'no' ? [
    "Hva utfordret meg i dag og hvordan reagerte jeg?",
    "Hva er jeg takknemlig for akkurat nå?",
    "Hva lærte jeg om meg selv i dag?",
    "Hvordan stilte jeg opp for meg selv i dag?",
    "Hva ville gjort i morgen til en flott dag?"
  ] : [
    "What challenged me today and how did I respond?",
    "What am I grateful for right now?",
    "What's one thing I learned about myself today?",
    "How did I show up for myself today?",
    "What would make tomorrow a great day?"
  ];

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newEntry, setNewEntry] = useState({ content: '', mood: 'neutral', tags: [] });
  const [currentPrompt, setCurrentPrompt] = useState(prompts[0]);

  useEffect(() => {
    fetchEntries();
    setCurrentPrompt(prompts[Math.floor(Math.random() * prompts.length)]);
  }, [i18n.language]);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/journal`, { headers: getAuthHeader() });
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const createEntry = async () => {
    if (!newEntry.content.trim()) {
      toast.error('Please write something before saving');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/journal`,
        newEntry,
        { headers: getAuthHeader() }
      );
      setEntries([response.data, ...entries]);
      setNewEntry({ content: '', mood: 'neutral', tags: [] });
      setShowNew(false);
      toast.success('Journal entry saved');
    } catch (error) {
      toast.error('Failed to save entry');
    }
  };

  const deleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/journal/${entryId}`, { headers: getAuthHeader() });
      setEntries(entries.filter(e => e.id !== entryId));
      toast.success('Entry deleted');
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const getMoodIcon = (mood) => {
    const option = moodOptions.find(m => m.value === mood);
    if (!option) return null;
    const Icon = option.icon;
    return <Icon className={`h-4 w-4 ${option.color}`} />;
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="journal-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif font-bold text-2xl md:text-3xl text-foreground">Reflection Journal</h1>
            <p className="text-muted-foreground">Process experiences and build mental clarity</p>
          </div>
          {!showNew && (
            <Button 
              onClick={() => setShowNew(true)} 
              className="bg-primary text-primary-foreground rounded-full"
              data-testid="new-entry-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Entry
            </Button>
          )}
        </div>

        {/* New Entry Form */}
        <AnimatePresence>
          {showNew && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="glass neon-border">
                <CardContent className="p-6 space-y-4">
                  <div className="p-4 bg-primary/10 rounded-xl border border-primary/20">
                    <p className="font-mono text-xs text-primary/80 uppercase tracking-wider mb-1">Reflection Prompt</p>
                    <p className="text-foreground italic">{currentPrompt}</p>
                  </div>

                  <div className="space-y-2">
                    <Label>How are you feeling?</Label>
                    <div className="flex flex-wrap gap-2">
                      {moodOptions.map((mood) => {
                        const Icon = mood.icon;
                        return (
                          <Button
                            key={mood.value}
                            type="button"
                            variant={newEntry.mood === mood.value ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setNewEntry({ ...newEntry, mood: mood.value })}
                            className={`rounded-full ${newEntry.mood === mood.value ? 'bg-primary text-primary-foreground' : 'border-white/10'}`}
                            data-testid={`mood-${mood.value}`}
                          >
                            <Icon className={`h-4 w-4 mr-1 ${newEntry.mood === mood.value ? '' : mood.color}`} />
                            {mood.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="content">Your Reflection</Label>
                    <Textarea
                      id="content"
                      value={newEntry.content}
                      onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                      placeholder="Write freely... What's on your mind?"
                      className="bg-input/50 border-transparent focus:border-primary min-h-[200px] resize-none"
                      data-testid="journal-content-input"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => setShowNew(false)}
                      className="rounded-full"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={createEntry}
                      className="bg-primary text-primary-foreground rounded-full"
                      data-testid="save-entry-btn"
                    >
                      Save Entry
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Entries List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground animate-pulse">Loading entries...</p>
            </div>
          ) : entries.length === 0 ? (
            <Card className="bg-card/50 border-white/5">
              <CardContent className="p-12 text-center">
                <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-serif text-xl text-foreground mb-2">Start Your Journal</h3>
                <p className="text-muted-foreground mb-4">Reflection is the key to growth. Write your first entry.</p>
                <Button 
                  onClick={() => setShowNew(true)}
                  className="bg-primary text-primary-foreground rounded-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Write First Entry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <AnimatePresence>
              {entries.map((entry, index) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-card/50 border-white/5 hover:border-accent/30 transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-accent/20">
                            {getMoodIcon(entry.mood)}
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(entry.created_at), 'EEEE, MMMM d, yyyy')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(entry.created_at), 'h:mm a')}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteEntry(entry.id)}
                          className="hover:bg-destructive/20 hover:text-destructive"
                          data-testid={`delete-entry-${entry.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-foreground leading-relaxed whitespace-pre-wrap" data-testid={`entry-content-${entry.id}`}>
                        {entry.content}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>
    </Layout>
  );
}
